import React, { useState, useRef, useEffect } from "react";
import { Lead, Presupuesto, EmailMessage } from "@/api/entities";
import { invokeFunctions } from "@/api/integrations";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Filter, Mail, LayoutGrid, List, GraduationCap, ChevronRight, ChevronLeft, CalendarCheck, RefreshCw, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import LeadCard from "../components/leads/LeadCard";
import CreateLeadDialog from "../components/leads/CreateLeadDialog";
import CierreLeadDialog from "../components/leads/CierreLeadDialog";
import EmailThreadsList from "../components/dashboard/EmailThreadsList";
import ActividadesList from "../components/dashboard/ActividadesList";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { useToast } from "@/components/ui/use-toast";
import { useEntorno } from "@/lib/EntornoContext";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/AuthContext";
import {
  ESTADOS_LEADS,
  normalizeEstadoLead,
  ESTADOS_LEADS_ABIERTOS,
  ESTADOS_LEADS_CERRADOS,
  ESTADOS_NO_PENDIENTES,
} from "@/lib/estadosLeads";

const ESTADOS_PEDIDOS = {
  nuevo_pedido: { label: "Nuevo Pedido", color: "bg-green-500" },
  preparando_archivos: { label: "Preparando Archivos", color: "bg-blue-500" },
  en_produccion: { label: "En Producción", color: "bg-purple-500" },
  finalizado: { label: "Finalizado", color: "bg-yellow-500" },
  enviado: { label: "Enviado", color: "bg-orange-500" },
  facturado: { label: "Facturado", color: "bg-gray-500" }
};

const EMBUDOS = {
  recibidos: { label: "Leads Recibidos", description: "Clientes que nos contactan" },
  prospeccion: { label: "Prospección", description: "Prospección comercial activa" },
  pedidos: { label: "Pedidos", description: "Oportunidades ganadas" }
};

export default function Dashboard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [cierrePending, setCierrePending] = useState(null); // { leadId, lead, historial } al dropear en Cerrado
  const [cerrandoLead, setCerrandoLead] = useState(false);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterPendienteEmail, setFilterPendienteEmail] = useState(false);
  const [filterTipo, setFilterTipo] = useState("todos"); // "todos", "general", "tesis"
  const [filterOwner, setFilterOwner] = useState("all"); // "all", "mine"
  const [viewMode, setViewMode] = useState("kanban"); // "kanban", "emails" o "actividades"
  const [currentEmbudo, setCurrentEmbudo] = useState("recibidos");
  const [sortBy, setSortBy] = useState("pendientes_primero"); // default: pendientes (antiguos primero) → resto por fecha desc
  const [showClosedColumns, setShowClosedColumns] = useState(false);
  // Columna Cerrado (solo embudo 'recibidos'): colapsada por defecto. Cuando
  // está colapsada se muestra como tira vertical estrecha con count + importe;
  // las 3 columnas activas ocupan ancho equitativo.
  const [cerradoColapsado, setCerradoColapsado] = useState(true);
  // #8 Modo limpio (esconde franja superior + iconos de ruido). Persistido en localStorage.
  const [cleanMode, setCleanMode] = useState(() => {
    try { return localStorage.getItem('crm_clean_mode') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('crm_clean_mode', cleanMode ? '1' : '0'); } catch {}
  }, [cleanMode]);
  const [isCheckingEmails, setIsCheckingEmails] = useState(false);
  const [isPrioritizing, setIsPrioritizing] = useState(false);
  const scrollContainerRef = useRef(null);
  
  const { entorno, isPruebas } = useEntorno();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleBulkPriority = async () => {
    setIsPrioritizing(true);
    try {
      const response = await invokeFunctions('bulkPriorityUpdate', {});
      const processed = response?.data?.processed || 0;
      toast({
        title: "Priorización completada",
        description: `Se han clasificado ${processed} leads antiguos por IA`,
      });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (error) {
      console.error('Error en bulkPriorityUpdate:', error);
      toast({
        title: "Error",
        description: error?.response?.data?.error || error.message || "Error al priorizar leads",
        variant: "destructive"
      });
    } finally {
      setIsPrioritizing(false);
    }
  };

  const handleCheckEmails = async () => {
    setIsCheckingEmails(true);
    try {
      const response = await invokeFunctions('procesarEmailsEntrantes', { entorno: isPruebas ? 'test' : 'produccion' });
      const processed = response?.data?.processed || 0;
      toast({
        title: "Correos procesados",
        description: `Se han procesado ${processed} correos nuevos`,
      });
      // Refresh leads after processing emails
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (error) {
      console.error('Error procesando emails:', error);
      toast({
        title: "Error",
        description: error?.response?.data?.error || error.message || "Error al procesar correos",
        variant: "destructive"
      });
    } finally {
      setIsCheckingEmails(false);
    }
  };

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: () => Lead.list({}, { orderBy: 'created_date', orderDirection: 'desc' }),
  });

  // Suscripción en tiempo real para actualizar leads automáticamente
  useEffect(() => {
    const unsubscribe = Lead.subscribe((event) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    });
    return unsubscribe;
  }, [queryClient]);

  // Cargar todos los presupuestos de una vez para evitar errores 429
  const { data: allPresupuestos = [] } = useQuery({
    queryKey: ['all-presupuestos'],
    queryFn: () => Presupuesto.list({}, { orderBy: 'created_date', orderDirection: 'desc' }),
  });

  // Crear un mapa de presupuestos por lead_id
  const presupuestosPorLead = React.useMemo(() => {
    const map = {};
    allPresupuestos.forEach(p => {
      if (!map[p.lead_id]) {
        map[p.lead_id] = p;
      }
    });
    return map;
  }, [allPresupuestos]);

  // Cargar emails salientes para estado de tracking
  const { data: allOutboundEmails = [] } = useQuery({
    queryKey: ['outbound-emails'],
    queryFn: () => EmailMessage.list({ direction: 'outbound' }, { orderBy: 'created_date', orderDirection: 'desc' }),
    refetchInterval: 30000, // Refrescar cada 30 segundos para ver actualizaciones de tracking
  });

  // Suscripción en tiempo real para emails
  useEffect(() => {
    const unsubscribe = EmailMessage.subscribe((event) => {
      queryClient.invalidateQueries({ queryKey: ['outbound-emails'] });
    });
    return unsubscribe;
  }, [queryClient]);

  // Crear mapa de estado de email por lead (último email saliente enviado por nosotros)
  const emailStatusPorLead = React.useMemo(() => {
    const map = {};
    // allOutboundEmails ya está ordenado por -created_date (más reciente primero)
    // Solo queremos el último que NO tenga una respuesta del cliente después
    allOutboundEmails.forEach(email => {
      // Solo usar el primero que encontramos (el más reciente) para cada lead
      if (!map[email.lead_id]) {
        map[email.lead_id] = {
          delivered: email.delivered,
          opened: email.opened,
          clicked: email.clicked,
          bounced: email.bounced
        };
      }
    });
    return map;
  }, [allOutboundEmails]);

  const createLeadMutation = useMutation({
    mutationFn: (data) => Lead.create({ 
      ...data, 
      entorno: isPruebas ? 'test' : 'produccion' 
    }),
    onSuccess: async (newLead) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowCreateDialog(false);
      
      // Enviar alerta a Discord
      try {
        await invokeFunctions('sendDiscordAlert', {
          evento: 'lead_manual',
          lead_id: newLead.id
        });
      } catch (error) {
        console.error('Error enviando alerta Discord:', error);
      }
    },
  });

  const updateLeadMutation = useMutation({
    mutationFn: ({ id, data }) => Lead.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const leadId = result.draggableId;
    const newEstado = result.destination.droppableId;

    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.estado === newEstado) return;

    const historialEstados = lead.historial_estados || [];
    historialEstados.push({
      estado: newEstado,
      fecha: new Date().toISOString(),
      usuario: "Usuario"
    });

    // Drop a 'cerrado': pedir motivo en dialog antes de aplicar.
    if (newEstado === 'cerrado') {
      setCierrePending({ leadId, lead, historial: historialEstados });
      return;
    }

    const updateData = {
      estado: newEstado,
      historial_estados: historialEstados
    };

    // Si pasa a nuevo_pedido, cambiar embudo a pedidos
    if (newEstado === 'nuevo_pedido') {
      updateData.embudo = 'pedidos';
    }

    // Al cerrar (o salir del embudo recibidos), limpiar el flag de pendiente
    // de respuesta — el lead deja de estar bajo seguimiento activo.
    if (ESTADOS_NO_PENDIENTES.includes(newEstado) || newEstado === 'nuevo_pedido') {
      updateData.pendiente_email = false;
    }

    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: updateData
    });
  };

  const handleConfirmarCierreKanban = async (motivo) => {
    if (!cierrePending) return;
    setCerrandoLead(true);
    try {
      // Reemplazar la última entrada de historial con motivo incluido.
      const historial = [...cierrePending.historial];
      if (historial.length > 0) historial[historial.length - 1] = { ...historial[historial.length - 1], motivo };
      await updateLeadMutation.mutateAsync({
        id: cierrePending.leadId,
        data: {
          estado: 'cerrado',
          cerrado_motivo: motivo,
          cerrado_auto: false,
          cerrado_fecha: new Date().toISOString(),
          pendiente_email: false,
          historial_estados: historial,
        }
      });
      setCierrePending(null);
    } finally {
      setCerrandoLead(false);
    }
  };

  // Determinar los estados según el embudo actual
  const ESTADOS = currentEmbudo === 'pedidos' ? ESTADOS_PEDIDOS : ESTADOS_LEADS;

  const priorityOrder = { alta: 3, media: 2, baja: 1 };

  // Defensa contra docs huérfanos sin metadata vital (importación CSV Base44 rota,
  // ene 2026). Antes de #16 estos quedaban invisibles porque el groupBy era
  // estricto por estado; ahora normalizeEstadoLead(null)='nuevo' los colaba
  // todos en la columna Nuevo. Filtramos en origen.
  const filteredLeads = leads.filter(lead => {
    if (!lead.created_date && !lead.estado && !lead.embudo && !lead.origen) return false;

    const matchSearch = lead.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       lead.empresa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchEstado = filterEstado === "todos" || (
      currentEmbudo === 'recibidos'
        ? normalizeEstadoLead(lead.estado) === filterEstado
        : lead.estado === filterEstado
    );
    const matchPendienteEmail = !filterPendienteEmail || lead.pendiente_email === true;
    const matchEmbudo = (lead.embudo || "recibidos") === currentEmbudo;
    const matchTipo = filterTipo === "todos" || (lead.tipo || "general") === filterTipo;
    
    // Filtro por entorno
    const matchEntorno = entorno === 'test' 
      ? lead.entorno === 'test' 
      : (lead.entorno !== 'test');
      
    const matchOwner = filterOwner === "all" || (filterOwner === "mine" && lead.owner_id === user?.uid);
    
    return (matchSearch && matchEstado && matchPendienteEmail && matchEmbudo && matchTipo && matchEntorno && matchOwner);
  }).sort((a, b) => {
    const dateA = new Date(a.created_date).getTime();
    const dateB = new Date(b.created_date).getTime();

    // Default: dentro de cada columna, los pendientes (más antiguos primero)
    // suben arriba; luego el resto por fecha de creación descendente.
    // Más antiguo entre pendientes usa updated_date (proxy del último email
    // inbound — el cron actualiza updated_date al guardar la respuesta del
    // cliente). Fallback a created_date si no hay updated_date.
    if (sortBy === 'pendientes_primero') {
      const aPend = !!a.pendiente_email && !ESTADOS_NO_PENDIENTES.includes(a.estado);
      const bPend = !!b.pendiente_email && !ESTADOS_NO_PENDIENTES.includes(b.estado);
      if (aPend !== bPend) return aPend ? -1 : 1;
      if (aPend && bPend) {
        const aT = new Date(a.updated_date || a.created_date).getTime();
        const bT = new Date(b.updated_date || b.created_date).getTime();
        return aT - bT; // antiguo primero
      }
      return dateB - dateA; // resto: nuevo primero
    }

    if (sortBy === 'date_desc') {
      return dateB - dateA;
    }
    if (sortBy === 'date_asc') {
      return dateA - dateB;
    }
    if (sortBy === 'expected_value') {
      const valA = a.expected_value || 0;
      const valB = b.expected_value || 0;
      if (valA !== valB) return valB - valA; // Descendente por valor
    } else {
      const pA = priorityOrder[a.priority] || 0;
      const pB = priorityOrder[b.priority] || 0;
      if (pA !== pB) return pB - pA; // Prioridad descendente (alta primero)
    }

    // Si el criterio principal es igual, ordenar por fecha de creación descendente
    return dateB - dateA;
  });

  // Solo contar leads activos (no cerrados/perdidos/finalizados/enviados/facturados ni archivados/borrados).
  const pendientesCount = leads.filter(l => l.pendiente_email && !ESTADOS_NO_PENDIENTES.includes(l.estado)).length;

  // Para "recibidos" agrupamos normalizando los estados legacy a la nueva estructura.
  // Para otros embudos, comparación directa.
  const leadsGroupedByEstado = Object.keys(ESTADOS).reduce((acc, estado) => {
    if (currentEmbudo === 'recibidos') {
      acc[estado] = filteredLeads.filter(lead => normalizeEstadoLead(lead.estado) === estado);
    } else {
      acc[estado] = filteredLeads.filter(lead => lead.estado === estado);
    }
    return acc;
  }, {});

  // Separar estados principales de estados cerrados según embudo
  const mainEstados = currentEmbudo === 'pedidos'
    ? ['nuevo_pedido', 'preparando_archivos', 'en_produccion', 'finalizado']
    : ESTADOS_LEADS_ABIERTOS;
  const closedEstados = currentEmbudo === 'pedidos'
    ? ['enviado', 'facturado']
    : ESTADOS_LEADS_CERRADOS;

  const scrollToClosedColumns = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: 'smooth'
      });
      setShowClosedColumns(true);
    }
  };

  const scrollToMainColumns = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
      setShowClosedColumns(false);
    }
  };

  const topLeads = filteredLeads
    .filter((lead) => {
      const estadoNorm = currentEmbudo === 'recibidos' ? normalizeEstadoLead(lead.estado) : lead.estado;
      return lead.expected_value > 0 && !closedEstados.includes(estadoNorm);
    })
    .sort((a, b) => (b.expected_value || 0) - (a.expected_value || 0))
    .slice(0, 5);

  return (
    <div className="p-2 lg:p-4 max-w-[1800px] mx-auto h-screen flex flex-col overflow-hidden">
      <div className="mb-2 lg:mb-4 flex-shrink-0">
        {/* Header con título, embudo y botón nuevo */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-2 lg:mb-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{EMBUDOS[currentEmbudo].label}</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{EMBUDOS[currentEmbudo].description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={cleanMode ? "default" : "outline"}
              size="sm"
              onClick={() => setCleanMode(v => !v)}
              className={cleanMode ? "bg-gray-800 hover:bg-gray-700 text-white" : ""}
              title={cleanMode ? "Desactivar modo limpio" : "Activar modo limpio (oculta franjas e iconos de ruido)"}
            >
              {cleanMode ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              <span className="hidden lg:inline">{cleanMode ? "Limpio" : "Modo limpio"}</span>
            </Button>
            {!cleanMode && leads.some(l => !l.priority && (isPruebas ? l.entorno === 'test' : l.entorno !== 'test')) && (
              <Button
                onClick={handleBulkPriority}
                disabled={isPrioritizing}
                variant="outline"
                size="sm"
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isPrioritizing ? 'animate-spin' : ''}`} />
                Priorizar Existentes
              </Button>
            )}
            {Object.entries(EMBUDOS).map(([key, { label }]) => (
              <Button
                key={key}
                variant={currentEmbudo === key ? "default" : "outline"}
                onClick={() => setCurrentEmbudo(key)}
                className={currentEmbudo === key ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                size="sm"
              >
                {label}
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-white/20">
                  {leads.filter(l => (l.embudo || "recibidos") === key).length}
                </span>
              </Button>
            ))}
            <Button 
              onClick={handleCheckEmails}
              disabled={isCheckingEmails}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isCheckingEmails ? 'animate-spin' : ''}`} />
              Revisar Emails
            </Button>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nuevo Lead
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar por nombre, empresa o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 h-9 text-sm"
            />
          </div>
          
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
          >
            <option value="todos">Todos los estados</option>
            {Object.entries(ESTADOS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <Button
            variant={filterPendienteEmail ? "default" : "outline"}
            onClick={() => setFilterPendienteEmail(!filterPendienteEmail)}
            className={filterPendienteEmail ? "bg-red-500 hover:bg-red-600" : ""}
            size="sm"
          >
            <Mail className="w-4 h-4 mr-1" />
            <span className="hidden lg:inline">Pendientes</span>
            {pendientesCount > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${filterPendienteEmail ? 'bg-white text-red-500' : 'bg-red-500 text-white'}`}>
                {pendientesCount}
              </span>
            )}
          </Button>

          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
          >
            <option value="todos">Todos los tipos</option>
            <option value="general">General</option>
            <option value="tesis">Tesis</option>
          </select>

          <select
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">Todos los leads</option>
            <option value="mine">Mis leads</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-indigo-200 dark:border-indigo-800 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium"
          >
            <option value="pendientes_primero">Ordenar: Pendientes primero (default)</option>
            <option value="date_desc">Ordenar: Fecha (más nuevos primero)</option>
            <option value="date_asc">Ordenar: Fecha (más antiguos primero)</option>
            <option value="priority">Ordenar: Prioridad (Alertas)</option>
            <option value="expected_value">Ordenar: Valor Esperado 💎</option>
          </select>

          <div className="flex border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("kanban")}
              className={`rounded-none px-2 ${viewMode === "kanban" ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300" : ""}`}
              title="Kanban"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("emails")}
              className={`rounded-none px-2 ${viewMode === "emails" ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300" : ""}`}
              title="Emails"
            >
              <Mail className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("actividades")}
              className={`rounded-none px-2 ${viewMode === "actividades" ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300" : ""}`}
              title="Actividades"
            >
              <CalendarCheck className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {!cleanMode && topLeads.length > 0 && (
        <div className="mb-4 bg-fuchsia-50 dark:bg-fuchsia-950/20 border border-fuchsia-200 dark:border-fuchsia-800/30 rounded-xl p-3 shadow-sm flex-shrink-0">
          <h2 className="text-sm font-bold text-fuchsia-800 dark:text-fuchsia-300 flex items-center gap-1.5 mb-2.5">
            <span>🔥</span> Top Leads a trabajar
          </h2>
          <div className="flex flex-nowrap gap-3 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {topLeads.map(lead => (
              <div key={lead.id} className="min-w-[240px] md:min-w-[280px] bg-white dark:bg-gray-800 border-l-4 border-fuchsia-500 rounded-lg p-2.5 shadow-sm">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1 text-sm">{lead.nombre}</h4>
                  <span className="text-[10px] font-bold text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-100 dark:bg-fuchsia-900/50 px-1.5 py-0.5 rounded flex-shrink-0">
                    💎 {lead.expected_value}€
                  </span>
                </div>
                {lead.empresa && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mb-1">
                    {lead.empresa}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-100 dark:border-indigo-800">
                    🎯 {lead.close_probability}%
                  </span>
                  {lead.next_action && (
                    <span 
                      className="text-[10px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded border border-blue-100 dark:border-blue-800 line-clamp-1 cursor-help"
                      title={lead.ai_reason}
                    >
                      👉 {lead.next_action}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === "kanban" ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          {/* Mobile: grid normal */}
          <div className="block xl:hidden flex-1 overflow-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(ESTADOS).map(([estado, { label, color }]) => (
                <Droppable key={estado} droppableId={estado}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`rounded-xl border-2 ${
                        snapshot.isDraggingOver 
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950' 
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                      } transition-all duration-200`}
                    >
                      <div className={`${color} text-white p-4 rounded-t-xl`}>
                        <h3 className="font-semibold text-sm">{label}</h3>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs opacity-90">{leadsGroupedByEstado[estado].length} leads</p>
                          <p className="text-xs font-semibold">
                            {leadsGroupedByEstado[estado].reduce((sum, l) => sum + (l.importe || 0), 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                          </p>
                        </div>
                      </div>
                      <div className="p-3 space-y-3 min-h-[300px]">
                        {leadsGroupedByEstado[estado].map((lead, index) => (
                          <LeadCard
                            key={lead.id}
                            lead={lead}
                            index={index}
                            presupuesto={presupuestosPorLead[lead.id]}
                            emailStatus={emailStatusPorLead[lead.id]}
                            columnLeadIds={leadsGroupedByEstado[estado].map(l => l.id)}
                            columnEstado={estado}
                            cleanMode={cleanMode}
                          />
                        ))}
                        {provided.placeholder}
                        {leadsGroupedByEstado[estado].length === 0 && (
                          <div className="text-center text-gray-400 dark:text-gray-600 text-sm py-8">
                            No hay leads en este estado
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </div>

          {/* Desktop: columnas fijas + scroll */}
          <div className="hidden xl:flex flex-col flex-1 relative overflow-hidden">
            <div 
              ref={scrollContainerRef}
              className="flex gap-3 overflow-x-auto flex-1 pb-2"
              style={{ scrollBehavior: 'smooth' }}
            >
              {/* Columnas principales — equitativas cuando Cerrado está colapsada en 'recibidos' */}
              {mainEstados.map((estado) => {
                const { label, color } = ESTADOS[estado];
                const colWidthClass = (currentEmbudo === 'recibidos' && cerradoColapsado)
                  ? 'flex-1 min-w-[200px]'
                  : 'flex-shrink-0 w-[calc((100%-2.25rem)/4)] min-w-[200px]';
                return (
                  <Droppable key={estado} droppableId={estado}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`rounded-lg border-2 ${colWidthClass} flex flex-col ${
                          snapshot.isDraggingOver
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                        } transition-all duration-200`}
                      >
                        <div className={`${color} text-white p-2.5 rounded-t-lg flex-shrink-0`}>
                          <h3 className="font-semibold text-xs">{label}</h3>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-[10px] opacity-90">{leadsGroupedByEstado[estado].length} leads</p>
                            <p className="text-[10px] font-semibold">
                              {leadsGroupedByEstado[estado].reduce((sum, l) => sum + (l.importe || 0), 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                            </p>
                          </div>
                        </div>
                        <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                          {leadsGroupedByEstado[estado].map((lead, index) => (
                            <LeadCard
                            key={lead.id}
                            lead={lead}
                            index={index}
                            presupuesto={presupuestosPorLead[lead.id]}
                            emailStatus={emailStatusPorLead[lead.id]}
                            columnLeadIds={leadsGroupedByEstado[estado].map(l => l.id)}
                            columnEstado={estado}
                            cleanMode={cleanMode}
                          />
                          ))}
                          {provided.placeholder}
                          {leadsGroupedByEstado[estado].length === 0 && (
                            <div className="text-center text-gray-400 dark:text-gray-600 text-xs py-4">
                              No hay leads en este estado
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}

              {/* Columnas cerradas — colapsables a tira estrecha en 'recibidos' */}
              {closedEstados.map((estado) => {
                const { label, color } = ESTADOS[estado];
                const colapsada = currentEmbudo === 'recibidos' && cerradoColapsado;
                const count = leadsGroupedByEstado[estado].length;
                const total = leadsGroupedByEstado[estado].reduce((sum, l) => sum + (l.importe || 0), 0);
                return (
                  <Droppable key={estado} droppableId={estado}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`rounded-lg border-2 flex-shrink-0 flex flex-col ${
                          colapsada ? 'w-[60px]' : 'w-[220px]'
                        } ${
                          snapshot.isDraggingOver
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                        } transition-all duration-200`}
                      >
                        {colapsada ? (
                          <button
                            type="button"
                            onClick={() => setCerradoColapsado(false)}
                            className={`${color} text-white rounded-t-lg flex-shrink-0 w-full text-left hover:brightness-110`}
                            title="Expandir Cerrado"
                          >
                            <div className="p-2 flex flex-col items-center gap-1">
                              <ChevronLeft className="w-4 h-4" />
                              <span className="text-[10px] font-semibold writing-mode-vertical-rl" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                {label}
                              </span>
                            </div>
                          </button>
                        ) : (
                          <div className={`${color} text-white p-2.5 rounded-t-lg flex-shrink-0`}>
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-xs">{label}</h3>
                              {currentEmbudo === 'recibidos' && (
                                <button
                                  type="button"
                                  onClick={() => setCerradoColapsado(true)}
                                  className="opacity-80 hover:opacity-100"
                                  title="Colapsar Cerrado"
                                >
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <p className="text-[10px] opacity-90">{count} leads</p>
                              <p className="text-[10px] font-semibold">
                                {total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                              </p>
                            </div>
                          </div>
                        )}
                        {colapsada ? (
                          <div className="flex-1 flex flex-col items-center justify-center px-1 py-2 gap-2 overflow-hidden">
                            <span className="text-base font-bold text-gray-700 dark:text-gray-300">{count}</span>
                            <span className="text-[9px] text-gray-500 dark:text-gray-400 text-center break-all">
                              {total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                            </span>
                            {provided.placeholder}
                          </div>
                        ) : (
                          <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                            {leadsGroupedByEstado[estado].map((lead, index) => (
                              <LeadCard
                                key={lead.id}
                                lead={lead}
                                index={index}
                                presupuesto={presupuestosPorLead[lead.id]}
                                emailStatus={emailStatusPorLead[lead.id]}
                                columnLeadIds={leadsGroupedByEstado[estado].map(l => l.id)}
                                columnEstado={estado}
                                cleanMode={cleanMode}
                              />
                            ))}
                            {provided.placeholder}
                            {leadsGroupedByEstado[estado].length === 0 && (
                              <div className="text-center text-gray-400 dark:text-gray-600 text-xs py-4">
                                No hay leads en este estado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>

            {/* Botones de navegación */}
            <button
              onClick={showClosedColumns ? scrollToMainColumns : scrollToClosedColumns}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-l-lg shadow-lg transition-all duration-200 flex items-center gap-1"
              title={showClosedColumns ? "Ver columnas principales" : "Ver cerrados"}
            >
              {showClosedColumns ? (
                <ChevronLeft className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          </div>
        </DragDropContext>
      ) : viewMode === "emails" ? (
        <div className="flex-1 overflow-auto">
          <EmailThreadsList />
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-4">
          <ActividadesList />
        </div>
      )}

      <CreateLeadDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={(data) => createLeadMutation.mutate(data)}
        isLoading={createLeadMutation.isPending}
        defaultEmbudo={currentEmbudo}
      />

      <CierreLeadDialog
        open={!!cierrePending}
        onClose={() => setCierrePending(null)}
        onSubmit={handleConfirmarCierreKanban}
        isLoading={cerrandoLead}
      />
    </div>
  );
}